import React, { useState, useEffect } from "react";
import "./TimeTrailsTask.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { TimeTrailsSidebar } from "./TimeTrailsSidebar";
import { TimeTrailsTaskDetails } from "./TimeTrailsTaskDetails";
import { SubmissionSuccessScreen } from "./SubmissionSuccessScreen";
import { getTimeTrialStatus } from "../../utils";
import timeTrailsEmpty from "../../assets/timetrails/time_trails_empty.svg";
import {
  useGetUserTimeTrailsQuery,
  useCreateTimeTrailSubmissionMutation,
} from "../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import { selectCurrentToken } from "../../redux-toolkit/features/auth/authSlice";
import { useAlert } from "../../hooks/useAlert";
import { validateTaskSubmissionImageFile } from "../../utils/taskSubmissionImage";

const normalizeUrlInput = (value = "") =>
  value.trim().replace(/^(https?:\/\/)+/i, "");

const toHttpsUrl = (value = "") => {
  const normalized = normalizeUrlInput(value);
  return normalized ? `https://${normalized}` : "";
};

const ensureHttpsProtocol = (value = "") => toHttpsUrl(value);

export const TimeTrailsTask = () => {
  const navigate = useNavigate();
  const token = useSelector(selectCurrentToken);
  const { displayAlert } = useAlert();
  const [formData, setFormData] = useState({
    selectedFile: null,
    url: "",
    comments: "",
    urlCleared: false,
    commentsCleared: false,
  });
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isResubmitSuccess, setIsResubmitSuccess] = useState(false);
  const [urlError, setUrlError] = useState("");

  const isValidHttpUrl = (value) => {
    try {
      const normalizedValue = toHttpsUrl(value);
      if (!normalizedValue) return false;
      const parsedUrl = new URL(normalizedValue);
      const hasValidProtocol =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      const hasRequiredDotInHost = parsedUrl.hostname.includes(".");
      const hasValidDomain = /^[a-z0-9.-]+$/i.test(parsedUrl.hostname);
      const hasNoInvalidHyphenPlacement = parsedUrl.hostname
        .split(".")
        .every(
          (label) => label && !label.startsWith("-") && !label.endsWith("-"),
        );

      return (
        hasValidProtocol &&
        hasRequiredDotInHost &&
        hasValidDomain &&
        hasNoInvalidHyphenPlacement
      );
    } catch (error) {
      return false;
    }
  };

  const validateUrlField = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setUrlError("");
      return true;
    }

    if (!isValidHttpUrl(trimmedValue)) {
      setUrlError("Please add a valid URL");
      return false;
    }

    setUrlError("");
    return true;
  };

  // Fetch user time trials using Redux
  const {
    data: timeTrials,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useGetUserTimeTrailsQuery(undefined, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });

  // Create submission mutation
  const [createSubmission, { isLoading: isSubmitting }] =
    useCreateTimeTrailSubmissionMutation();

  // Auto-select the first active time trail when data loads or when navigating to the page
  useEffect(() => {
    if (timeTrials?.data?.length > 0) {
      // Check if there's a task parameter in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const taskParam = urlParams.get("task");

      if (taskParam) {
        // If there's a task parameter, try to select that specific task
        const taskFromUrl = timeTrials.data.find(
          (trial) => trial._id === taskParam,
        );
        if (taskFromUrl) {
          setSelectedTaskId(taskParam);
          return;
        }
      }

      // Check if current selection is valid
      const currentSelectionValid = timeTrials.data.some(
        (trial) => trial._id === selectedTaskId,
      );

      // If no URL parameter, invalid task ID, or no selection, auto-select active trial
      if (!selectedTaskId || !currentSelectionValid) {
        // Find the first active time trail (PENDING status)
        const activeTrial = timeTrials.data.find((trial) => {
          const status = getTimeTrialStatus(trial);
          return status === "PENDING";
        });

        // Only select a trial if there's an active (PENDING) trial
        if (activeTrial) {
          setSelectedTaskId(activeTrial._id);

          // Update URL to reflect the selected task
          const currentUrl = window.location.search;
          const newUrl = `?task=${activeTrial._id}`;

          // Only update URL if it's different from current URL
          if (currentUrl !== newUrl) {
            navigate(`/quest/time-trails/?task=${activeTrial._id}`, {
              replace: true,
            });
          }
        } else {
          // No active trial found, don't select anything
          setSelectedTaskId(null);
        }
      }
    }
  }, [timeTrials, selectedTaskId, navigate]);

  // Handle navigation events to ensure auto-selection works when navigating to the page
  useEffect(() => {
    const handleNavigation = () => {
      if (timeTrials?.data?.length > 0) {
        // Check URL parameters on navigation
        const urlParams = new URLSearchParams(window.location.search);
        const taskParam = urlParams.get("task");

        // Check if current selection is valid
        const currentSelectionValid = timeTrials.data.some(
          (trial) => trial._id === selectedTaskId,
        );

        if (!taskParam && (!selectedTaskId || !currentSelectionValid)) {
          // If no task parameter and no valid selection, auto-select active trial
          const activeTrial = timeTrials.data.find((trial) => {
            const status = getTimeTrialStatus(trial);
            return status === "PENDING";
          });

          if (activeTrial) {
            setSelectedTaskId(activeTrial._id);

            // Update URL to reflect the selected task
            const currentUrl = window.location.search;
            const newUrl = `?task=${activeTrial._id}`;

            // Only update URL if it's different from current URL
            if (currentUrl !== newUrl) {
              navigate(`/quest/time-trails/?task=${activeTrial._id}`, {
                replace: true,
              });
            }
          } else {
            // No active trial found, don't select anything
            setSelectedTaskId(null);
          }
        }
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", handleNavigation);

    // Also check on initial load
    handleNavigation();

    return () => {
      window.removeEventListener("popstate", handleNavigation);
    };
  }, [timeTrials, selectedTaskId, navigate]);

  // Get the currently selected task
  const currentTask =
    timeTrials?.data?.find((task) => task._id === selectedTaskId) ||
    timeTrials?.data?.[0];

  // Initialize form data with existing submission when task changes
  useEffect(() => {
    if (currentTask?.taskCompletion) {
      const existingSubmission = currentTask.taskCompletion;

      setFormData({
        selectedFile: null, // File objects can't be restored from URL
        url: ensureHttpsProtocol(existingSubmission.attachments?.url || ""),
        comments: existingSubmission.comments || "", // Comments is now at root level
        existingImageRemoved: false, // Track if existing image was removed in resubmit mode
        urlCleared: false,
        commentsCleared: false,
      });
    } else {
      // Reset form when switching to a task without submission
      setFormData({
        selectedFile: null,
        url: "",
        comments: "",
        existingImageRemoved: false,
        urlCleared: false,
        commentsCleared: false,
      });
    }
  }, [currentTask]);

  const handleTaskSelect = (taskId) => {
    setSelectedTaskId(taskId);
    navigate(`/quest/time-trails/?task=${taskId}`);
    // Form data will be reset/initialized in useEffect when currentTask changes
  };

  const showFileValidationError = (message, event) => {
    if (event?.target) {
      event.target.value = "";
    }
    displayAlert({
      open: true,
      message,
      type: "error",
      timeout: "2000",
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validationError = validateTaskSubmissionImageFile(file);
    if (validationError) {
      showFileValidationError(validationError, event);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedFile: file,
    }));
  };

  const validateSelectedFileBeforeSubmit = () => {
    if (!formData.selectedFile) return true;
    const validationError = validateTaskSubmissionImageFile(formData.selectedFile);
    if (validationError) {
      displayAlert({
        open: true,
        message: validationError,
        type: "error",
        timeout: "2000",
      });
      return false;
    }
    return true;
  };

  const handleUrlUpload = () => {
    if (formData.url.trim()) {
      // TODO: Implement URL upload logic
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedTaskId) {
        console.error("No task selected");
        return;
      }

      // Validate that at least one submission method is provided
      if (!formData.selectedFile && !formData.url.trim()) {
        console.error("Please provide either a file or URL");
        // TODO: Show validation error message
        return;
      }

      if (!validateUrlField(formData.url)) {
        return;
      }

      if (!validateSelectedFileBeforeSubmit()) {
        return;
      }

      // Prepare submission data
      const submissionData = {
        comments: formData?.comments,
        url: toHttpsUrl(formData.url),
      };

      // Handle file upload if file is selected
      if (formData.selectedFile) {
        // Create FormData for file upload
        const formDataToSend = new FormData();
        formDataToSend.append("file", formData.selectedFile);
        formDataToSend.append("comments", submissionData.comments);
        formDataToSend.append("url", submissionData.url);

        // Submit file using FormData
        const result = await createSubmission({
          timetrialId: selectedTaskId,
          submissionData: formDataToSend,
        }).unwrap();
      } else if (formData.url.trim()) {
        // Handle URL submission
        submissionData.url = toHttpsUrl(formData.url);

        // Submit URL using JSON
        const result = await createSubmission({
          timetrialId: selectedTaskId,
          submissionData,
        }).unwrap();
      }

      // Reset form after successful submission
      setFormData({
        selectedFile: null,
        url: "",
        comments: "",
      });

      // Refetch data to get the latest submission
      await refetch();

      // Show success screen
      setIsResubmitSuccess(false); // Ensure it's false for new submissions
      setShowSuccessScreen(true);
    } catch (error) {
      displayAlert({
        open: true,
        message:
          error?.data?.message ?? "Submission failed. Please try again.",
        type: "error",
        timeout: "2000",
      });
    }
  };

  const handleResubmit = async () => {
    try {
      if (!selectedTaskId) {
        console.error("No task selected");
        return false;
      }

      // Get current task to check existing submission
      const currentTask = timeTrials?.data?.find(
        (task) => task._id === selectedTaskId,
      );
      const hasExistingImage =
        currentTask?.taskCompletion?.attachments?.filename;
      const hasExistingUrl = currentTask?.taskCompletion?.attachments?.url;

      // Validate that at least one submission method is provided
      if (
        !formData.selectedFile &&
        !formData.url.trim() &&
        !hasExistingImage &&
        !hasExistingUrl
      ) {
        console.error("Please provide either a file or URL");
        // TODO: Show validation error message
        return false;
      }

      if (!validateUrlField(formData.url)) {
        return false;
      }

      if (!validateSelectedFileBeforeSubmit()) {
        return false;
      }

      // Prepare submission data
      const submissionData = {
        comments: formData?.comments,
        url: toHttpsUrl(formData.url),
      };

      // Handle file upload if new file is selected
      if (formData.selectedFile) {
        // Create FormData for file upload
        const formDataToSend = new FormData();
        formDataToSend.append("file", formData.selectedFile);
        formDataToSend.append("comments", submissionData.comments);
        formDataToSend.append("url", submissionData.url);

        // Submit file using FormData
        const result = await createSubmission({
          timetrialId: selectedTaskId,
          submissionData: formDataToSend,
        }).unwrap();
      } else {
        // Handle case where no new file is selected but existing image should be preserved
        // or URL submission
        if (hasExistingImage && !formData.existingImageRemoved) {
          // Check if it's an S3 URL (starts with http/https)
          if (hasExistingImage.startsWith("http")) {
            // Convert existing S3 URL to File object
            const existingFile = await convertS3UrlToFile(hasExistingImage);

            if (existingFile) {
              // Create FormData for file upload with converted file
              const formDataToSend = new FormData();
              formDataToSend.append("file", existingFile);
              formDataToSend.append("comments", submissionData.comments);
              formDataToSend.append("url", submissionData.url);

              // Submit file using FormData
              const result = await createSubmission({
                timetrialId: selectedTaskId,
                submissionData: formDataToSend,
              }).unwrap();
            } else {
              console.error("Failed to convert S3 URL to file");
              // Fallback to URL submission if conversion fails
              submissionData.url = toHttpsUrl(formData.url);
              const result = await createSubmission({
                timetrialId: selectedTaskId,
                submissionData,
              }).unwrap();
            }
          } else {
            // Handle local file paths or other non-HTTP URLs
            submissionData.file = hasExistingImage;
            const result = await createSubmission({
              timetrialId: selectedTaskId,
              submissionData,
            }).unwrap();
          }
        } else if (formData.url.trim()) {
          // Handle URL submission
          submissionData.url = toHttpsUrl(formData.url);

          const result = await createSubmission({
            timetrialId: selectedTaskId,
            submissionData,
          }).unwrap();
        }
      }

      // Reset form after successful resubmission
      setFormData({
        selectedFile: null,
        url: "",
        comments: "",
        existingImageRemoved: false,
        urlCleared: false,
        commentsCleared: false,
      });

      // Refetch data to update the UI
      await refetch();

      // Show success message
      // You can add a success alert here
      setIsResubmitSuccess(true);
      setShowSuccessScreen(true);
      return true;
    } catch (error) {
      displayAlert({
        open: true,
        message:
          error?.data?.message ?? "Resubmit failed. Please try again.",
        type: "error",
        timeout: "2000",
      });
      return false;
    }
  };

  const handleCancel = () => {
    setFormData({
      selectedFile: null,
      url: "",
      comments: "",
      urlCleared: false,
      commentsCleared: false,
    });
    setUrlError("");
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleUrlChange = (e) => {
    const value = ensureHttpsProtocol(e.target.value);
    setFormData((prev) => ({
      ...prev,
      url: value,
      urlCleared: value === "", // Mark as cleared if empty
    }));
    // Validate only on submit/resubmit; clear stale error while user edits.
    if (urlError) {
      setUrlError("");
    }
  };

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      comments: value,
      commentsCleared: value === "", // Mark as cleared if empty
    }));
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      selectedFile: null,
      // Clear the existing submission image reference in resubmit mode
      existingImageRemoved: true,
    }));
  };

  const handleRetry = () => {
    refetch();
  };

  const handlePreview = () => {
    setShowSuccessScreen(false);
    setIsResubmitSuccess(false); // Reset resubmit success state
    // Refetch the data to get the latest submission
    refetch().then((result) => {
      // Force re-initialization of form data after refetch
      if (result.data?.data) {
        const updatedTask = result.data.data.find(
          (task) => task._id === selectedTaskId,
        );
        if (updatedTask?.taskCompletion) {
          const existingSubmission = updatedTask.taskCompletion;
          setFormData({
            selectedFile: null,
            url: ensureHttpsProtocol(existingSubmission.attachments?.url || ""),
            comments: existingSubmission.comments || "",
            existingImageRemoved: false,
            urlCleared: false,
            commentsCleared: false,
          });
        }
      }
    });
  };

  // Function to convert S3 URL to File object
  const convertS3UrlToFile = async (s3Url) => {
    try {
      const response = await fetch(s3Url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();

      // Extract filename from URL or use a default name
      const urlParts = s3Url.split("/");
      let filename = urlParts[urlParts.length - 1] || "existing-image.jpg";

      // Remove query parameters if present
      filename = filename.split("?")[0];

      // Ensure filename has an extension
      if (!filename.includes(".")) {
        filename += ".jpg";
      }

      // Create a File object from the blob
      const file = new File([blob], filename, { type: blob.type });
      return file;
    } catch (error) {
      console.error("Error converting S3 URL to file:", error);
      return null;
    }
  };

  const handleResubmitModeEnter = () => {
    // Initialize form data for resubmit mode
    if (currentTask?.taskCompletion) {
      const existingSubmission = currentTask.taskCompletion;
      setFormData({
        selectedFile: null,
        url: ensureHttpsProtocol(existingSubmission.attachments?.url || ""),
        comments: existingSubmission.comments || "", // Comments is now at root level
        existingImageRemoved: false, // Ensure existing image is not marked as removed
        urlCleared: false,
        commentsCleared: false,
      });
    }
  };

  // Show loading state (initial load only — keep content on background refetch)
  if (loading || !token) {
    return (
      <div className="timetrails-task">
        <div className="timetrails-task__header">
          <div className="timetrails-task__header-left">
            <div className="timetrails-task__status">
              <span>Time trial | active</span>
            </div>
            <div className="timetrails-task__logo">
              <span className="logo-the">The</span>
              <span className="logo-quest">Quest</span>
              <span className="logo-icon">Q</span>
            </div>
          </div>
        </div>
        <div className="timetrails-task__container">
          <div className="timetrails-task__loading">
            <div className="loading-spinner"></div>
            <p>Loading time trials...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state only when there is nothing usable to display
  if (isError && !timeTrials?.data) {
    const errorMessage =
      error?.data?.message ||
      (error?.status === "FETCH_ERROR"
        ? "Unable to reach the server. Please check your connection and try again."
        : "Failed to load time trials. Please try again later.");

    return (
      <div className="timetrails-task">
        <div className="timetrails-task__header">
          <div className="timetrails-task__header-left">
            <div className="timetrails-task__status">
              <span>Time trial | active</span>
            </div>
            <div className="timetrails-task__logo">
              <span className="logo-the">The</span>
              <span className="logo-quest">Quest</span>
              <span className="logo-icon">Q</span>
            </div>
          </div>
        </div>
        <div className="timetrails-task__container">
          <div className="timetrails-task__error">
            <p>{errorMessage}</p>
            <button onClick={handleRetry} className="retry-btn">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state when no time trials or no active trials selected
  if (timeTrials?.data?.length === 0 || !selectedTaskId) {
    return (
      <div className="timetrails-task">
        <div className="timetrails-task__header">
          <div className="timetrails-task__header-left">
            <div className="timetrails-task__status">
              <span>Time trial | active</span>
            </div>
            <div className="timetrails-task__logo">
              <span className="logo-the">The</span>
              <span className="logo-quest">Quest</span>
              <span className="logo-icon">Q</span>
            </div>
          </div>
        </div>
        <div className="timetrails-task__container">
          {/* Sidebar Component */}
          <TimeTrailsSidebar
            timeTrials={timeTrials}
            selectedTaskId={selectedTaskId}
            sidebarOpen={sidebarOpen}
            onTaskSelect={handleTaskSelect}
            onToggleSidebar={toggleSidebar}
          />

          {/* Empty State */}
          <div className="timetrails-task__main">
            <div className="timetrails-task__empty">
              <div className="empty-state-content">
                <div className="empty-state-image">
                  <img
                    src={timeTrailsEmpty}
                    alt="No Time Trials Found"
                    className="empty-illustration"
                  />
                </div>
                <div className="empty-state-text">
                  <h2 className="empty-title">
                    {timeTrials?.data?.length === 0
                      ? "No Time Trial Found!"
                      : "No Active Time Trials!"}
                  </h2>
                  <p className="empty-subtitle">
                    {timeTrials?.data?.length === 0
                      ? "Aah oh! Looks like no Time Trial Task currently"
                      : "All time trials are completed or expired"}
                  </p>
                  <p className="empty-description">
                    {timeTrials?.data?.length === 0 ? "Task currently" : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show submission success screen
  if (showSuccessScreen) {
    return (
      <div className="timetrails-task">
        {/* Top Navigation */}
        <div className="timetrails-task__header">
          <div className="timetrails-task__header-left">
            <div className="timetrails-task__status">
              <span>Time trial | active</span>
            </div>
            <div className="timetrails-task__logo">
              <span className="logo-the">The</span>
              <span className="logo-quest">Quest</span>
              <span className="logo-icon">Q</span>
            </div>
          </div>
        </div>

        <div className="timetrails-task__container">
          {/* Sidebar Component */}
          <TimeTrailsSidebar
            timeTrials={timeTrials}
            selectedTaskId={selectedTaskId}
            sidebarOpen={sidebarOpen}
            onTaskSelect={handleTaskSelect}
            onToggleSidebar={toggleSidebar}
          />

          {/* Submission Success Component */}
          <SubmissionSuccessScreen
            onPreview={handlePreview}
            taskName={currentTask?.task}
            isResubmit={isResubmitSuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="timetrails-task">
      {/* Top Navigation */}
      <div className="timetrails-task__header">
        <div className="timetrails-task__header-left">
          <div className="timetrails-task__status">
            <span>Time trial | active</span>
          </div>
          <div className="timetrails-task__logo">
            <span className="logo-the">The</span>
            <span className="logo-quest">Quest</span>
            <span className="logo-icon">Q</span>
          </div>
        </div>
      </div>

      <div className="timetrails-task__container">
        {/* Sidebar Component */}
        <TimeTrailsSidebar
          timeTrials={timeTrials}
          selectedTaskId={selectedTaskId}
          sidebarOpen={sidebarOpen}
          onTaskSelect={handleTaskSelect}
          onToggleSidebar={toggleSidebar}
        />

        {/* Task Details Component */}
        <TimeTrailsTaskDetails
          currentTask={currentTask}
          formData={formData}
          urlError={urlError}
          onFileUpload={handleFileUpload}
          onUrlChange={handleUrlChange}
          onUrlUpload={handleUrlUpload}
          onCommentsChange={handleNoteChange}
          onRemoveFile={handleRemoveFile}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onResubmit={handleResubmit}
          onResubmitModeEnter={handleResubmitModeEnter}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};
